import { useState } from "react";
import { Link } from "react-router-dom";
import type { BusinessListingMarketplaceItemResponse } from "../../types/businessListingTypes";
import { formatBusinessListingCompensation, formatBusinessListingDuration, formatBusinessListingJobType } from "../../utils/BusinessListingUtils";
import { formatCityStateZip } from "../../utils/LocationUtils";
import clipboardIcon from "../../assets/icons/clipboard.svg";
import Button from "../ui/Button/Button";

export type BusinessListingFeedback = {
  tone: "success" | "error";
  message: string;
};

type BusinessListingDetailGridProps = {
  listing: BusinessListingMarketplaceItemResponse;
  canApplyAsTeen: boolean;
  isOwnListing?: boolean;
  isApplying: boolean;
  hasApplied: boolean;
  feedback: BusinessListingFeedback | null;
  onApply: () => void;
};

const buildBusinessListingShareUrl = (listing: BusinessListingMarketplaceItemResponse) => {
  const path = "/gigs";
  const params = new URLSearchParams();
  params.set("board", "BUSINESS_LISTINGS");
  params.set("listingId", listing.id);

  if (typeof window === "undefined") {
    return `${path}?${params.toString()}`;
  }

  const url = new URL(path, window.location.origin);
  params.forEach((value, key) => url.searchParams.set(key, value));
  return url.toString();
};

const copyTextToClipboard = async (value: string) => {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }

  const textarea = document.createElement("textarea");
  textarea.value = value;
  textarea.setAttribute("readonly", "true");
  textarea.style.position = "fixed";
  textarea.style.left = "-9999px";
  document.body.appendChild(textarea);
  textarea.select();

  try {
    const copied = document.execCommand("copy");
    if (!copied) {
      throw new Error("Copy command failed");
    }
  } finally {
    document.body.removeChild(textarea);
  }
};

const BusinessListingDetailGrid = ({
  listing,
  canApplyAsTeen,
  isOwnListing = false,
  isApplying,
  hasApplied,
  feedback,
  onApply,
}: BusinessListingDetailGridProps) => {
  const canApply = canApplyAsTeen && !isOwnListing && !hasApplied;
  const [shareFeedback, setShareFeedback] = useState<BusinessListingFeedback | null>(null);

  const onCopyShareLink = async () => {
    try {
      await copyTextToClipboard(buildBusinessListingShareUrl(listing));
      setShareFeedback({ tone: "success", message: "Business listing link copied." });
    } catch {
      setShareFeedback({ tone: "error", message: "Unable to copy link. Try again from your browser controls." });
    }
  };

  return (
    <div className="rounded-lg border border-myteengig-border bg-myteengig-background p-4">
      <div className="grid gap-4 lg:grid-cols-[2fr_1fr]">
        <section className="rounded-lg border border-myteengig-border bg-white p-4">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">Description</h3>
          <p className="mt-2 whitespace-pre-wrap text-sm text-myteengig-text-primary">{listing.description}</p>
          {listing.jobType === "VOLUNTEER" ? (
            <div className="mt-4 rounded-lg border border-myteengig-border bg-myteengig-background/70 px-4 py-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">Community service hours</p>
              <p className="mt-2 text-sm font-medium text-myteengig-text-primary">
                {listing.communityServiceHoursEligible ? "Available" : "Not offered"}
              </p>
              {listing.communityServiceHoursEligible ? (
                <p className="mt-1 whitespace-pre-wrap break-words text-sm text-myteengig-text-primary">
                  {listing.communityServiceProofDetails}
                </p>
              ) : null}
            </div>
          ) : null}
        </section>

        <aside className="rounded-lg border border-myteengig-border bg-white p-4">
          <dl className="space-y-3 text-sm text-myteengig-text-primary">
            <div>
              <dt className="text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">Business</dt>
              <dd className="mt-1">{listing.businessName || "Business"}</dd>
            </div>
            <div>
              <dt className="text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">Job type</dt>
              <dd className="mt-1">{formatBusinessListingJobType(listing.jobType)}</dd>
            </div>
            <div>
              <dt className="text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">Compensation</dt>
              <dd className="mt-1">{formatBusinessListingCompensation(listing.payType, listing.minPayAmountCents, listing.maxPayAmountCents, listing.jobType)}</dd>
            </div>
            <div>
              <dt className="text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">Location</dt>
              <dd className="mt-1">{formatCityStateZip(listing)}</dd>
            </div>
            {listing.estimatedDurationMinutes ? (
              <div>
                <dt className="text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">Usual shift</dt>
                <dd className="mt-1">{formatBusinessListingDuration(listing.estimatedDurationMinutes)}</dd>
              </div>
            ) : null}
          </dl>

          <div className="mt-4 space-y-3">
            <Button
              variant="outline"
              onClick={() => {
                void onCopyShareLink();
              }}
              className="flex w-full items-center justify-center gap-2 bg-white text-myteengig-text-primary hover:border-myteengig-primary hover:text-myteengig-primary"
              aria-label={`Copy share link for ${listing.title}`}
            >
              <img src={clipboardIcon} alt="" className="h-4 w-4" aria-hidden="true" />
              <span>Copy Link</span>
            </Button>

            {canApplyAsTeen ? (
              <>
                <Button
                  variant="primary"
                  onClick={onApply}
                  disabled={!canApply || isApplying}
                  className="w-full"
                >
                  {isOwnListing ? "Your posting" : hasApplied ? "Applied" : isApplying ? "Applying..." : "Apply for Job"}
                </Button>
                {hasApplied && listing.activeApplicationId ? (
                  <Link
                    to={`/teen/business-applications?applicationId=${listing.activeApplicationId}`}
                    className="inline-flex w-full items-center justify-center rounded-lg border border-myteengig-border bg-white px-4 py-2 text-sm font-medium text-myteengig-text-primary transition hover:border-myteengig-primary hover:text-myteengig-primary"
                  >
                    View Business Applications
                  </Link>
                ) : null}
              </>
            ) : null}
          </div>

          {shareFeedback ? (
            <p className={`mt-3 text-sm font-medium ${shareFeedback.tone === "success" ? "text-emerald-700" : "text-red-600"}`}>
              {shareFeedback.message}
            </p>
          ) : null}

          {feedback ? (
            <p className={`mt-3 text-sm font-medium ${feedback.tone === "success" ? "text-emerald-700" : "text-red-600"}`}>
              {feedback.message}
            </p>
          ) : null}

          {hasApplied ? (
            <p className="mt-3 text-sm text-myteengig-text-muted">
              Manage submitted business listing applications from your Business Applications page.
            </p>
          ) : null}
        </aside>
      </div>
    </div>
  );
};

export default BusinessListingDetailGrid;
